import React, { useEffect, useState } from "react";
import Paginated from "../../components/Paginated";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Eye, EyeOff, Pencil } from "lucide-react";
import { hasRole, rolesOf } from "../../lib/roles";

const ROLES = ["super_admin", "major_admin", "admin", "catalogue_admin", "salesperson", "karigar", "astrologer", "billing_desk"];
// Staff who report to one or more administrators (get the reporting picker).
const REPORTS_TO_ROLES = ["salesperson", "karigar", "billing_desk"];

const ADMIN_ROLES = ["super_admin", "major_admin", "admin"];

/* One person, several jobs: pick extra roles on top of the primary one. They log
   in once and get the combined access of everything ticked here. */
function ExtraRolesPicker({ primary, value, onChange, testidPrefix }) {
  const toggle = (r) => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  return (
    <div className="border border-line rounded-lg p-3 bg-pearl/50" data-testid={`${testidPrefix}-extra-roles`}>
      <div className="eyebrow text-[10px] mb-1">
        Also holds these roles <span className="normal-case tracking-normal text-ash">— optional, same login, combined access</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {ROLES.filter((r) => r !== primary).map((r) => {
          const on = value.includes(r);
          return (
            <button type="button" key={r} onClick={() => toggle(r)} aria-pressed={on}
              data-testid={`${testidPrefix}-extra-${r}`}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${on ? "bg-maroon text-pearl border-maroon" : "bg-white text-ink border-line hover:border-maroon"}`}>
              {on ? "✓ " : "+ "}{r.replace(/_/g, " ")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* Reporting picker — which administrator(s) a salesperson reports to (max 2).
   With one administrator, their phone number automatically becomes the EOD
   WhatsApp number. With two, the number must be specified explicitly. */
function ReportingPicker({ admins, value, waNumber, onChange, onWaChange, testidPrefix }) {
  const toggle = (id) => {
    const cur = new Set(value || []);
    if (cur.has(id)) cur.delete(id);
    else {
      if (cur.size >= 2) { toast.error("A salesperson can report to at most 2 administrators"); return; }
      cur.add(id);
    }
    onChange([...cur]);
  };
  const selected = admins.filter((a) => (value || []).includes(a.id));
  const single = selected.length === 1 ? selected[0] : null;
  return (
    <div className="border border-line rounded-lg p-3 bg-pearl/50 space-y-2">
      <div className="eyebrow text-[10px]">Reports to (select 1 or 2 administrators)</div>
      <div className="flex flex-wrap gap-2">
        {admins.map((a) => {
          const on = (value || []).includes(a.id);
          return (
            <button key={a.id} type="button" onClick={() => toggle(a.id)}
              className={`text-xs rounded-full border px-3 py-1.5 transition-all ${on ? "bg-maroon text-pearl border-maroon" : "border-line bg-white hover:border-maroon"}`}
              data-testid={`${testidPrefix}-reporting-${a.id}`}>
              {a.name} · {a.role.replace("_", " ")}
            </button>
          );
        })}
        {admins.length === 0 && <span className="text-xs text-ash">No administrators found.</span>}
      </div>
      <label className="block text-sm">
        <span className="eyebrow text-[10px]">
          EOD report WhatsApp number{" "}
          {selected.length === 2
            ? <b className="text-red-600 normal-case">(required — two administrators selected)</b>
            : single
              ? <span className="text-ash normal-case">(blank = {single.name}'s number {single.phone ? `· ${single.phone}` : ""} is used automatically)</span>
              : <span className="text-ash normal-case">(where the daily workflow report goes on “Share on WhatsApp”)</span>}
        </span>
        <input maxLength={10} inputMode="numeric" placeholder={single?.phone || "10-digit mobile"}
          required={selected.length === 2}
          className="w-full border border-line px-3 py-2 bg-white mt-1 rounded-lg"
          value={waNumber} onChange={(e) => onWaChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
          data-testid={`${testidPrefix}-report-wa`}/>
      </label>
    </div>
  );
}

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "salesperson", extra_roles: [], password: "", store: "Swayambhu Gold", reporting_to: [], report_wa_number: "", commission_pct: "", specialization: "" });
  const [editUser, setEditUser] = useState(null); // user being edited
  const [editForm, setEditForm] = useState({ name: "", phone: "", role: "", extra_roles: [], store: "", active: true, password: "", specialization: "", commission_pct: "", reporting_to: [], report_wa_number: "" });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const load = () => api.get("/api/crm/users").then((r) => setUsers(r.data || []));
  useEffect(() => {
    load();
    api.get("/api/crm/stores").then((r) => setStores(r.data || [])).catch(() => {});
  }, []);
  const admins = users.filter((u) => hasRole(u, ADMIN_ROLES) && u.active !== false);
  const rolesNow = (f) => [f.role, ...(f.extra_roles || [])];

  const submit = async (e) => {
    e.preventDefault();
    const mine = rolesNow(form);
    if (mine.some((r) => REPORTS_TO_ROLES.includes(r)) && (form.reporting_to || []).length === 0) {
      toast.error(`Select the administrator this ${mine.filter((r) => REPORTS_TO_ROLES.includes(r))[0].replace("_", " ")} reports to`); return;
    }
    if ((form.reporting_to || []).length === 2 && !form.report_wa_number.trim()) {
      toast.error("With two administrators, specify the WhatsApp number the EOD report goes to"); return;
    }
    if (mine.includes("astrologer") && (form.commission_pct === "" || form.commission_pct === null)) {
      toast.error("Specify the commission (%) kept for this astrologer — it is mandatory"); return;
    }
    if (form.phone && !/^\d{10}$/.test(form.phone)) {
      toast.error("Phone must be exactly 10 digits"); return;
    }
    try {
      const payload = { ...form, commission_pct: form.commission_pct === "" ? null : +form.commission_pct };
      await api.post("/api/crm/users", payload);
      toast.success("User created");
      setShowForm(false);
      setShowPassword(false);
      setForm({ name: "", email: "", phone: "", role: "salesperson", extra_roles: [], password: "", store: "Swayambhu Gold", reporting_to: [], report_wa_number: "", commission_pct: "", specialization: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || "", phone: u.phone || "", role: u.role, extra_roles: u.extra_roles || [], store: u.store || "", active: u.active !== false, password: "", specialization: u.specialization || "", commission_pct: u.commission_pct ?? "", reporting_to: u.reporting_to || [], report_wa_number: u.report_wa_number || "" });
    setShowEditPassword(false);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (editForm.phone && !/^\d{10}$/.test(editForm.phone)) {
      toast.error("Phone must be exactly 10 digits"); return;
    }
    const payload = {
      name: editForm.name,
      phone: editForm.phone,
      role: editForm.role,
      extra_roles: (editForm.extra_roles || []).filter((r) => r !== editForm.role),
      store: editForm.store,
      active: editForm.active,
      reporting_to: editForm.reporting_to,
      report_wa_number: editForm.report_wa_number,
    };
    if (editForm.password.trim()) payload.password = editForm.password.trim();
    const mineEdit = rolesNow(editForm);
    if (mineEdit.includes("karigar")) payload.specialization = editForm.specialization;
    if (mineEdit.includes("astrologer") && editForm.commission_pct !== "") payload.commission_pct = +editForm.commission_pct;
    try {
      await api.patch(`/api/crm/users/${editUser.id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/crm/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow">Hierarchy</div>
          <h1 className="font-display text-4xl text-maroon">Users & Roles</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-maroon" data-testid="add-user-btn"><Plus size={14}/> Add User</button>
      </div>
      <Paginated items={users} pageSize={15} label="users" scroller>
        {(pageUsers) => (
      <div className="bg-white border border-line">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>
              {["Name", "Email", "Role", "Store", "Reports To", "Status", "Actions"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {pageUsers.map((u) => (
              <tr key={u.id} className="border-b border-line/60" data-testid={`user-row-${u.id}`}>
                <td className="p-3">{u.name}</td>
                <td className="p-3 text-ash">{u.email}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1" data-testid={`user-roles-${u.id}`}>
                    {rolesOf(u).map((r, i) => (
                      <span key={r} className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${i === 0 ? "bg-gold/20 text-maroon" : "bg-pearl border border-line text-ash"}`} title={i === 0 ? "Primary role" : "Additional role"}>{r.replace("_", " ")}</span>
                    ))}
                  </div>
                </td>
                <td className="p-3 text-ash">{u.store}</td>
                <td className="p-3 text-ash text-xs">{(u.reporting_to_names || []).join(", ") || "—"}{u.report_wa_number ? <div className="text-[10px]">EOD WA: {u.report_wa_number}</div> : null}</td>
                <td className="p-3">{u.active ? <span className="text-green-700">Active</span> : <span className="text-ash">Disabled</span>}</td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => openEdit(u)} className="inline-flex items-center gap-1 text-xs text-maroon hover:underline" data-testid={`edit-user-${u.id}`}>
                      <Pencil size={12}/> Edit
                    </button>
                    <button onClick={() => remove(u)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid={`delete-user-${u.id}`}>
                      <Trash2 size={12}/> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        )}
      </Paginated>

      {/* Create user modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white border border-line p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl text-maroon">Create user</div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18}/></button>
            </div>
            <input data-testid="new-user-name" placeholder="Name" required className="w-full border border-line px-3 py-2 bg-pearl" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
            <input data-testid="new-user-email" placeholder="Email" type="email" required className="w-full border border-line px-3 py-2 bg-pearl" value={form.email} onChange={(e) => setForm({...form, email: e.target.value.toLowerCase()})}/>
            <input data-testid="new-user-phone" placeholder="10-digit mobile" maxLength={10} inputMode="numeric" className="w-full border border-line px-3 py-2 bg-pearl" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10)})}/>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Primary role</span>
              <select data-testid="new-user-role" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.role} onChange={(e) => setForm({...form, role: e.target.value, extra_roles: (form.extra_roles || []).filter((r) => r !== e.target.value)})}>
                {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
              </select>
            </label>
            <ExtraRolesPicker primary={form.role} value={form.extra_roles || []} onChange={(v) => setForm({...form, extra_roles: v})} testidPrefix="new-user"/>
            {form.role !== "astrologer" ? (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Store / showroom this user is assigned to</span>
                <select data-testid="new-user-store" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.store} onChange={(e) => setForm({...form, store: e.target.value})}>
                  {!stores.some((s) => s.name === form.store) && <option value={form.store}>{form.store || "Select store…"}</option>}
                  {stores.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </label>
            ) : (
              <div className="bg-gold/10 border border-gold/40 p-3 text-xs" data-testid="new-user-astro-note">
                Astrologers are <b>open for everyone</b> — they are not tied to any single store or showroom.
              </div>
            )}
            {rolesNow(form).includes("astrologer") && (
              <div className="border border-line rounded-lg p-3 bg-pearl/50 space-y-2">
                <label className="block text-sm">
                  <span className="eyebrow text-[10px]">Commission kept by Swayambhu Gold (%) — mandatory</span>
                  <input required type="number" min="0" max="100" step="0.5" placeholder="e.g. 20"
                    className="w-full border border-line px-3 py-2 bg-white mt-1 rounded-lg"
                    value={form.commission_pct}
                    onChange={(e) => setForm({...form, commission_pct: e.target.value})}
                    data-testid="new-user-commission"/>
                </label>
                <label className="block text-sm">
                  <span className="eyebrow text-[10px]">Specializations / USP (shown to customers)</span>
                  <input placeholder="e.g. Vedic · Marriage compatibility · Career" className="w-full border border-line px-3 py-2 bg-white mt-1 rounded-lg"
                    value={form.specialization} onChange={(e) => setForm({...form, specialization: e.target.value})}
                    data-testid="new-user-specialization"/>
                </label>
              </div>
            )}
            {rolesNow(form).some((r) => REPORTS_TO_ROLES.includes(r)) && (
              <ReportingPicker
                admins={admins}
                value={form.reporting_to}
                waNumber={form.report_wa_number}
                onChange={(ids) => setForm({ ...form, reporting_to: ids })}
                onWaChange={(v) => setForm({ ...form, report_wa_number: v })}
                testidPrefix="new-user"
              />
            )}
            <div className="relative">
              <input
                data-testid="new-user-password"
                type={showPassword ? "text" : "password"}
                placeholder="Initial password"
                required
                className="w-full border border-line px-3 py-2 pr-10 bg-pearl"
                value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ash hover:text-maroon"
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
            <button type="submit" className="btn-maroon w-full justify-center" data-testid="create-user-submit">Create</button>
          </form>
        </div>
      )}

      {/* Edit user modal */}
      {editUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setEditUser(null)}>
          <form onSubmit={submitEdit} className="bg-white border border-line p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl text-maroon">Edit user</div>
              <button type="button" onClick={() => setEditUser(null)}><X size={18}/></button>
            </div>
            <div className="text-xs text-ash">{editUser.email} <span className="text-[10px]">(email cannot be changed)</span></div>
            <input data-testid="edit-user-name" placeholder="Name" required className="w-full border border-line px-3 py-2 bg-pearl" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})}/>
            <input data-testid="edit-user-phone" placeholder="10-digit mobile" maxLength={10} inputMode="numeric" className="w-full border border-line px-3 py-2 bg-pearl" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value.replace(/\D/g, "").slice(0, 10)})}/>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Primary role</span>
              <select data-testid="edit-user-role" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value, extra_roles: (editForm.extra_roles || []).filter((r) => r !== e.target.value)})}>
                {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
              </select>
            </label>
            <ExtraRolesPicker primary={editForm.role} value={editForm.extra_roles || []} onChange={(v) => setEditForm({...editForm, extra_roles: v})} testidPrefix="edit-user"/>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Store / showroom</span>
              <select data-testid="edit-user-store" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={editForm.store} onChange={(e) => setEditForm({...editForm, store: e.target.value})}>
                {!stores.some((s) => s.name === editForm.store) && <option value={editForm.store}>{editForm.store || "Select store…"}</option>}
                {stores.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </label>
            {rolesNow(editForm).some((r) => REPORTS_TO_ROLES.includes(r)) && (
              <ReportingPicker
                admins={admins.filter((a) => a.id !== editUser.id)}
                value={editForm.reporting_to}
                waNumber={editForm.report_wa_number}
                onChange={(ids) => setEditForm({ ...editForm, reporting_to: ids })}
                onWaChange={(v) => setEditForm({ ...editForm, report_wa_number: v })}
                testidPrefix="edit-user"
              />
            )}
            {rolesNow(editForm).includes("karigar") && (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Specialization / USP (e.g. Gold · Pendants, Rings)</span>
                <input data-testid="edit-user-specialization" placeholder="What this karigar is expert in" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={editForm.specialization} onChange={(e) => setEditForm({...editForm, specialization: e.target.value})}/>
              </label>
            )}
            {rolesNow(editForm).includes("astrologer") && (
              <label className="block text-sm"><span className="eyebrow text-[10px]">Swayambhu Gold commission % (can differ per astrologer)</span>
                <input data-testid="edit-user-commission" type="number" placeholder="e.g. 10" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={editForm.commission_pct} onChange={(e) => setEditForm({...editForm, commission_pct: e.target.value})}/>
              </label>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({...editForm, active: e.target.checked})} data-testid="edit-user-active"/>
              Active
            </label>
            <div className="relative">
              <input
                data-testid="edit-user-password"
                type={showEditPassword ? "text" : "password"}
                placeholder="New password (leave blank to keep current)"
                className="w-full border border-line px-3 py-2 pr-10 bg-pearl"
                value={editForm.password}
                onChange={(e) => setEditForm({...editForm, password: e.target.value})}
              />
              <button
                type="button"
                onClick={() => setShowEditPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ash hover:text-maroon"
                aria-label={showEditPassword ? "Hide password" : "Show password"}
                data-testid="toggle-edit-password"
              >
                {showEditPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
            <p className="text-[11px] text-ash -mt-1">For security the current password can't be shown (it's stored encrypted). Type a new one here to reset it.</p>
            <button type="submit" className="btn-maroon w-full justify-center" data-testid="save-user-edit">Save Changes</button>
          </form>
        </div>
      )}
    </div>
  );
}
